import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Shield, ArrowLeft, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { saveUserInfo, UserInfo } from '../../utils/auth';
import { getApiV1Url } from '../../config/api';

// 쿠키 유틸리티 함수
const setCookie = (name: string, value: string, days: number = 365): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const ACCOUNT_CODE_COOKIE = 'lastAccountCode';

// Types
type LoginType = 'root' | 'iam';

interface RootLoginForm {
  rootUserId: string;
  rootPassword: string;
}

interface IamLoginForm {
  accountCode: string;
  userId: string;
  password: string;
}

interface FormErrors {
  [key: string]: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [loginType, setLoginType] = useState<LoginType>('root');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Root user form data
  const [rootForm, setRootForm] = useState<RootLoginForm>({
    rootUserId: '',
    rootPassword: ''
  });
  
  // IAM user form data
  const [iamForm, setIamForm] = useState<IamLoginForm>({
    accountCode: '',
    userId: '',
    password: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  // 컴포넌트 마운트 시 쿠키에서 계정 코드 읽기
  useEffect(() => {
    const savedAccountCode = getCookie(ACCOUNT_CODE_COOKIE);
    if (savedAccountCode) {
      setIamForm(prev => ({
        ...prev,
        accountCode: savedAccountCode
      }));
    }
  }, []);

  const handleRootInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    // name 속성이 "username" 또는 "password"로 변경되었으므로 매핑 필요
    const fieldName = name === 'username' ? 'rootUserId' : name === 'password' ? 'rootPassword' : name;
    setRootForm(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleIamInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    // name 속성이 "username" 또는 "password"로 변경되었으므로 매핑 필요
    const fieldName = name === 'username' ? 'userId' : name === 'password' ? 'password' : name;
    setIamForm(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateRootLogin = (): boolean => {
    const newErrors: FormErrors = {};
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!rootForm.rootUserId) {
      newErrors.rootUserId = '루트 사용자 ID를 입력해주세요';
    } else if (!emailRegex.test(rootForm.rootUserId)) {
      newErrors.rootUserId = '유효한 이메일 주소를 입력해주세요';
    }
    
    if (!rootForm.rootPassword) {
      newErrors.rootPassword = '비밀번호를 입력해주세요';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateIamLogin = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!iamForm.accountCode) {
      newErrors.accountCode = '계정 코드를 입력해주세요';
    }
    
    if (!iamForm.userId) {
      newErrors.userId = '사용자 ID를 입력해주세요';
    }
    
    if (!iamForm.password) {
      newErrors.password = '비밀번호를 입력해주세요';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    
    let isValid: boolean = false;
    if (loginType === 'root') {
      isValid = validateRootLogin();
    } else {
      isValid = validateIamLogin();
    }
    
    if (!isValid) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (loginType === 'root') {
        // 루트 사용자 로그인 API 호출
        const response = await fetch(getApiV1Url('/accounts/auth'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rootUserId: rootForm.rootUserId,
            rootPassword: rootForm.rootPassword
          })
        });

        if (response.ok) {
          // 서버 응답에서 사용자 정보 가져오기
          const responseData = await response.json();
          
          // 서버 응답 로그 출력
          console.log('로그인 성공 - 서버 응답 데이터:', responseData);
          
          // 응답 데이터 구조: { timestamp, data: { code, name, status, rootUserId, passwordChangeRequired, ... } }
          const userData = responseData.data;
          
          // 루트 사용자 정보 저장
          const userInfo: UserInfo = {
            accountCode: userData.code, // code가 accountCode
            userId: userData.code, // 루트사용자는 accountCode를 userId로 사용
            userType: 'root',
            loginId: rootForm.rootUserId
          };
          
          // 저장할 사용자 정보 로그 출력
          console.log('저장할 사용자 정보:', userInfo);
          
          saveUserInfo(userInfo);
          
          // 비밀번호 변경이 필요한 경우 비밀번호 변경 화면으로 이동
          if (userData.passwordChangeRequired === true) {
            navigate('/auth/change-password');
          } else {
            // 성공 시 메인 페이지로 이동
            navigate('/main');
          }
        } else {
          // 서버에서 온 에러 메시지 파싱
          const errorData = await response.json();
          const errorMessage = errorData.errors?.error?.[0]?.message || errorData.message || '로그인에 실패했습니다.';
          throw new Error(errorMessage);
        }
      } else {
        // IAM 사용자 로그인 API 호출
        const response = await fetch(getApiV1Url(`/accounts/${iamForm.accountCode}/users/auth`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: iamForm.userId,
            password: iamForm.password
          })
        });

        if (response.ok) {
          // 서버 응답에서 사용자 정보 가져오기
          const responseData = await response.json();
          
          // 서버 응답 로그 출력
          console.log('IAM 로그인 성공 - 서버 응답 데이터:', responseData);
          
          // 응답 데이터 구조: { timestamp, errors, data: { accountCode, userId, passwordChangeRequired, ... } }
          const userData = responseData.data;
          
          if (!userData) {
            throw new Error('서버 응답에 사용자 데이터가 없습니다.');
          }
          
          // IAM 사용자 정보 저장
          const userInfo: UserInfo = {
            accountCode: userData.accountCode,
            userId: userData.userId,
            userType: 'iam',
            loginId: iamForm.userId
          };
          
          // 저장할 사용자 정보 로그 출력
          console.log('IAM 사용자 - 저장할 사용자 정보:', userInfo);
          
          saveUserInfo(userInfo);
          
          // 계정 코드를 쿠키에 저장 (1년간 유지)
          setCookie(ACCOUNT_CODE_COOKIE, userData.accountCode, 365);
          
          // 비밀번호 변경이 필요한 경우 비밀번호 변경 화면으로 이동
          if (userData.passwordChangeRequired === true) {
            navigate('/auth/change-password');
          } else {
            // 성공 시 메인 페이지로 이동
            navigate('/main');
          }
        } else {
          // 서버에서 온 에러 메시지 파싱
          const errorData = await response.json();
          const errorMessage = errorData.errors?.error?.[0]?.message || errorData.message || '로그인에 실패했습니다.';
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      const serverMessage = error instanceof Error ? error.message : '';
      const defaultMessage = '로그인에 실패했습니다. 다시 시도해주세요.';
      const fullMessage = serverMessage ? `${defaultMessage}\n\n서버 메시지: ${serverMessage}` : defaultMessage;
      alert(fullMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = (): void => {
    // 랜딩 페이지로 이동
    navigate('/');
  };

  const handleLoginTypeChange = (type: LoginType): void => {
    setLoginType(type);
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
            >
              <img src="/flos-icon.svg" alt="Flos" className="w-8 h-8 mr-2" />
              <span className="text-xl font-semibold text-gray-900">Flos</span>
            </div>
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              <span>돌아가기</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">로그인</h1>
            <p className="mt-2 text-gray-600">Flos 콘솔에 접속하세요</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Login Type Tabs */}
            <div className="flex border-b">
              <button
                type="button"
                onClick={() => handleLoginTypeChange('root')}
                className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                  loginType === 'root'
                    ? 'text-gray-900 border-b-2 border-gray-900 bg-gray-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center justify-center">
                  <Shield className="w-5 h-5 mr-2" />
                  <span>루트 사용자</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleLoginTypeChange('iam')}
                className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                  loginType === 'iam'
                    ? 'text-gray-900 border-b-2 border-gray-900 bg-gray-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center justify-center">
                  <User className="w-5 h-5 mr-2" />
                  <span>계정 사용자</span>
                </div>
              </button>
            </div>

            {/* Login Forms */}
            <div className="p-6">
              {loginType === 'root' ? (
                // Root User Login Form
                <form onSubmit={handleSubmit} method="post" autoComplete="on">
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        루트 사용자는 모든 권한을 가진 관리자입니다. 일상적인 작업에는 계정 사용자 로그인을 권장합니다.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="rootUserId" className="block text-sm font-medium text-gray-700 mb-2">
                        루트 사용자 ID (이메일)
                      </label>
                      <input
                        type="email"
                        id="rootUserId"
                        name="username"
                        value={rootForm.rootUserId}
                        onChange={handleRootInputChange}
                        autoComplete="username"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.rootUserId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="admin@example.com"
                        maxLength={50}
                      />
                      {errors.rootUserId && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.rootUserId}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="rootPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        비밀번호
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="rootPassword"
                          name="password"
                          value={rootForm.rootPassword}
                          onChange={handleRootInputChange}
                          autoComplete="current-password"
                          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                            errors.rootPassword ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="비밀번호 입력"
                          maxLength={50}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {errors.rootPassword && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.rootPassword}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-6 space-y-3">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-3 bg-gray-900 text-white rounded-lg transition-colors flex items-center justify-center ${
                          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            로그인 중...
                          </>
                        ) : (
                          '로그인'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleBack}
                        className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        돌아가기
                      </button>
                    </div>

                    {/* Additional Links */}
                    <div className="mt-6 text-center space-y-2">
                      <button 
                        type="button"
                        onClick={() => navigate('/accounts/create')}
                        className="text-sm text-blue-600 hover:underline block mx-auto"
                      >
                        계정이 없으신가요? 가입하기
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                // IAM User Login Form
                <form onSubmit={handleSubmit} method="post" autoComplete="on">
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        계정 사용자는 부여된 권한 내에서만 작업이 가능합니다. 일상적인 작업에 권장됩니다.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="accountCode" className="block text-sm font-medium text-gray-700 mb-2">
                        계정 코드
                      </label>
                      <input
                        type="text"
                        id="accountCode"
                        name="accountCode"
                        value={iamForm.accountCode}
                        onChange={handleIamInputChange}
                        autoComplete="organization"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.accountCode ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="계정 코드"
                        maxLength={20}
                      />
                      {errors.accountCode && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.accountCode}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
                        사용자 ID
                      </label>
                      <input
                        type="text"
                        id="userId"
                        name="username"
                        value={iamForm.userId}
                        onChange={handleIamInputChange}
                        autoComplete="username"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.userId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="사용자 ID 입력"
                        maxLength={50}
                      />
                      {errors.userId && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.userId}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                        비밀번호
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          name="password"
                          value={iamForm.password}
                          onChange={handleIamInputChange}
                          autoComplete="current-password"
                          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                            errors.password ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="비밀번호 입력"
                          maxLength={50}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.password}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-6 space-y-3">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-3 bg-gray-900 text-white rounded-lg transition-colors flex items-center justify-center ${
                          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            로그인 중...
                          </>
                        ) : (
                          '로그인'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleBack}
                        className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        돌아가기
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              보안을 위해 공용 컴퓨터에서는 사용 후 반드시 로그아웃해주세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;